import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ReferrerTypeAlias,
  ReferrerTypeDefinition,
  ReferrerTypeReviewQueueItem,
  ReferrerTypeService,
} from '../services/referrerTypeService';

const MATCH_TIERS = [
  {
    id: 'exact',
    title: 'Exact match',
    score: 100,
    description: 'Normalised organisation name matches an exact alias.',
    kinds: ['exact'] as const,
  },
  {
    id: 'keyword',
    title: 'Keyword match',
    score: 90,
    description: 'Token-boundary keyword hit on organisation name (drops to 70 on multi-type conflict).',
    kinds: ['keyword', 'synonym'] as const,
  },
  {
    id: 'fuzzy',
    title: 'Fuzzy match',
    score: '70–93',
    description: 'Jaro-Winkler similarity band. Auto-accept ≥94; 70–93 enters review queue.',
    kinds: [] as const,
  },
];

function AliasTable({
  aliases,
  onDeactivate,
}: {
  aliases: ReferrerTypeAlias[];
  onDeactivate: (aliasId: string) => void;
}) {
  if (aliases.length === 0) {
    return <p className="empty-state-inline">No aliases configured.</p>;
  }
  return (
    <div className="referrer-type-table-wrap">
      <table className="referrer-type-table">
        <thead>
          <tr>
            <th>Type</th>
            <th>Alias</th>
            <th>Kind</th>
            <th>Source</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {aliases.map((alias) => (
            <tr key={alias.id}>
              <td>{alias.display_name || alias.type_name}</td>
              <td>{alias.alias_text}</td>
              <td>{alias.match_kind}</td>
              <td>{alias.source}</td>
              <td>{alias.status}</td>
              <td>
                {alias.status === 'active' && (
                  <button type="button" className="btn btn-link" onClick={() => onDeactivate(alias.id)}>
                    Deactivate
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export const ReferrerTypeManagerPanel: React.FC = () => {
  const [definitions, setDefinitions] = useState<ReferrerTypeDefinition[]>([]);
  const [aliases, setAliases] = useState<ReferrerTypeAlias[]>([]);
  const [reviewQueue, setReviewQueue] = useState<ReferrerTypeReviewQueueItem[]>([]);
  const [selectedTypeId, setSelectedTypeId] = useState<string>('all');
  const [newAliasText, setNewAliasText] = useState('');
  const [newMatchKind, setNewMatchKind] = useState<'exact' | 'keyword' | 'synonym'>('exact');
  const [newTypeName, setNewTypeName] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [editingTypeId, setEditingTypeId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<Partial<ReferrerTypeDefinition>>({});
  const [error, setError] = useState<string | null>(null);
  const [queueWarning, setQueueWarning] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setQueueWarning(null);

    let types: ReferrerTypeDefinition[] = [];
    try {
      types = await ReferrerTypeService.listDefinitions();
      setDefinitions(types);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load referrer types');
      setDefinitions([]);
      setAliases([]);
      setReviewQueue([]);
      setLoading(false);
      return;
    }

    try {
      setReviewQueue(await ReferrerTypeService.listReviewQueue());
    } catch (err: unknown) {
      setReviewQueue([]);
      setQueueWarning(err instanceof Error ? err.message : 'Failed to load review queue');
    }

    try {
      if (selectedTypeId === 'all') {
        const allAliases = await Promise.all(types.map((type) => ReferrerTypeService.listAliases(type.id)));
        setAliases(allAliases.flat());
      } else {
        setAliases(await ReferrerTypeService.listAliases(selectedTypeId));
      }
    } catch (err: unknown) {
      setAliases([]);
      setError(err instanceof Error ? err.message : 'Failed to load aliases');
    } finally {
      setLoading(false);
    }
  }, [selectedTypeId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const exactAliases = useMemo(
    () => aliases.filter((a) => a.match_kind === 'exact' && a.status === 'active'),
    [aliases],
  );
  const keywordAliases = useMemo(
    () => aliases.filter((a) => (a.match_kind === 'keyword' || a.match_kind === 'synonym') && a.status === 'active'),
    [aliases],
  );

  const handleCreateAlias = async () => {
    if (!newAliasText.trim() || selectedTypeId === 'all') return;
    await ReferrerTypeService.createAlias(selectedTypeId, newAliasText.trim(), newMatchKind);
    setNewAliasText('');
    await loadData();
  };

  const handleCreateType = async () => {
    if (!newTypeName.trim() || !newDisplayName.trim()) return;
    await ReferrerTypeService.createDefinition(newTypeName.trim(), newDisplayName.trim());
    setNewTypeName('');
    setNewDisplayName('');
    await loadData();
  };

  const handleSaveType = async (typeId: string) => {
    await ReferrerTypeService.updateDefinition(typeId, {
      display_name: editDraft.display_name,
      description: editDraft.description ?? undefined,
      priority: editDraft.priority,
      status: editDraft.status,
    });
    setEditingTypeId(null);
    setEditDraft({});
    await loadData();
  };

  const handlePromote = async (item: ReferrerTypeReviewQueueItem, typeId: string) => {
    await ReferrerTypeService.promoteReviewItem(
      item.normalised_key,
      typeId,
      item.sample_source_text || item.normalised_key,
    );
    await loadData();
  };

  if (loading) {
    return <div className="referrer-type-panel loading">Loading referrer type configuration…</div>;
  }

  return (
    <div className="referrer-type-panel">
      {error && <div className="error-banner">{error}</div>}
      {queueWarning && <div className="warning-banner">{queueWarning}</div>}

      <section className="referrer-type-section skill-enum-section">
        <div className="section-heading-row">
          <h3>Allowed referrer types (enum)</h3>
          <span className="tier-score">AI schema source</span>
        </div>
        <p className="section-help">
          These values form the allowed enum for AI fallback classification. Active types appear in the extraction schema.
        </p>

        <div className="referrer-type-add-form">
          <input
            type="text"
            placeholder="type_name (e.g. gp)"
            value={newTypeName}
            onChange={(e) => setNewTypeName(e.target.value)}
          />
          <input
            type="text"
            placeholder="Display name"
            value={newDisplayName}
            onChange={(e) => setNewDisplayName(e.target.value)}
          />
          <button type="button" className="btn btn-success" onClick={handleCreateType}>
            Add type
          </button>
        </div>

        <div className="referrer-type-table-wrap">
          <table className="referrer-type-table">
            <thead>
              <tr>
                <th>type_name</th>
                <th>Display name</th>
                <th>Priority</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {definitions.map((type) => {
                const editing = editingTypeId === type.id;
                return (
                  <tr key={type.id}>
                    <td><code>{type.type_name}</code></td>
                    <td>
                      {editing ? (
                        <input
                          value={editDraft.display_name ?? ''}
                          onChange={(e) => setEditDraft((d) => ({ ...d, display_name: e.target.value }))}
                        />
                      ) : (
                        type.display_name
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <input
                          type="number"
                          value={editDraft.priority ?? type.priority}
                          onChange={(e) => setEditDraft((d) => ({ ...d, priority: Number(e.target.value) }))}
                          className="input-narrow"
                        />
                      ) : (
                        type.priority
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <select
                          value={editDraft.status ?? type.status}
                          onChange={(e) => setEditDraft((d) => ({ ...d, status: e.target.value }))}
                        >
                          <option value="active">active</option>
                          <option value="testing">testing</option>
                          <option value="inactive">inactive</option>
                        </select>
                      ) : (
                        type.status
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <>
                          <button type="button" className="btn btn-link" onClick={() => handleSaveType(type.id)}>
                            Save
                          </button>
                          <button
                            type="button"
                            className="btn btn-link"
                            onClick={() => {
                              setEditingTypeId(null);
                              setEditDraft({});
                            }}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-link"
                          onClick={() => {
                            setEditingTypeId(type.id);
                            setEditDraft({
                              display_name: type.display_name,
                              description: type.description ?? undefined,
                              priority: type.priority,
                              status: type.status,
                            });
                          }}
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="referrer-type-section">
        <h3>Matching tiers</h3>
        <div className="referrer-type-toolbar">
          <label htmlFor="referrer-type-filter">Filter aliases by type</label>
          <select
            id="referrer-type-filter"
            value={selectedTypeId}
            onChange={(e) => setSelectedTypeId(e.target.value)}
          >
            <option value="all">All types</option>
            {definitions.map((type) => (
              <option key={type.id} value={type.id}>
                {type.display_name}
              </option>
            ))}
          </select>
        </div>

        {selectedTypeId !== 'all' && (
          <div className="referrer-type-add-form">
            <input
              type="text"
              placeholder="Alias text"
              value={newAliasText}
              onChange={(e) => setNewAliasText(e.target.value)}
            />
            <select
              value={newMatchKind}
              onChange={(e) => setNewMatchKind(e.target.value as typeof newMatchKind)}
            >
              <option value="exact">Exact (100)</option>
              <option value="keyword">Keyword (90)</option>
              <option value="synonym">Synonym (90)</option>
            </select>
            <button type="button" className="btn btn-success" onClick={handleCreateAlias}>
              Add alias
            </button>
          </div>
        )}

        {MATCH_TIERS.map((tier) => (
          <div key={tier.id} className="match-tier-block">
            <div className="section-heading-row">
              <h4>{tier.title}</h4>
              <span className="tier-score">Score {tier.score}</span>
            </div>
            <p className="section-help">{tier.description}</p>
            {tier.id === 'fuzzy' ? (
              reviewQueue.length === 0 ? (
                <p className="empty-state-inline">No fuzzy-band items in review queue.</p>
              ) : (
                <div className="referrer-type-table-wrap">
                  <table className="referrer-type-table">
                    <thead>
                      <tr>
                        <th>Sample org</th>
                        <th>Occurrences</th>
                        <th>Last method</th>
                        <th>Confidence</th>
                        <th>Promote to exact</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reviewQueue.map((item) => (
                        <tr key={item.normalised_key}>
                          <td>{item.sample_source_text || item.normalised_key}</td>
                          <td>{item.occurrence_count}</td>
                          <td>{item.last_method || '—'}</td>
                          <td>{item.last_confidence ?? '—'}</td>
                          <td>
                            <select
                              defaultValue=""
                              onChange={(e) => {
                                if (e.target.value) {
                                  handlePromote(item, e.target.value);
                                  e.target.value = '';
                                }
                              }}
                            >
                              <option value="">Assign type…</option>
                              {definitions.map((type) => (
                                <option key={type.id} value={type.id}>
                                  {type.display_name}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : (
              <AliasTable
                aliases={tier.id === 'exact' ? exactAliases : keywordAliases}
                onDeactivate={(id) => ReferrerTypeService.deactivateAlias(id).then(loadData)}
              />
            )}
          </div>
        ))}
      </section>
    </div>
  );
};
